// ============================================================================
// FLIPCARD COMPONENT - Interactive Loan Payment Card
// ============================================================================
// Working Note: This component creates a flip card interface for loan payments
// that displays loan details on the front and payment form on the back.
// Supports multiple loan types (RD, GOLD, PERS, BIZ, VEH, TA, etc.) with specific validation rules.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Stack, Badge } from "react-bootstrap";
import "../../layouts/Flipcard.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoanDetailsModal from "../../components/LoanDetailsModal";
import { MODULEDUES } from "../../services/actions";
import GoldPaymentConfirmationModal from "../../components/GoldPaymentConfirmationModal";
import DepositScheme from "../../components/DepositScheme";

// ============================================================================
// COMPONENT DEFINITION & PROPS
// ============================================================================
const Flipcard = ({
  onMoreClick, // Callback for more details action
  loanTypeDesc, // Description of loan type
  Acc_number, // Account number
  Disb_amt, // Disbursed amount
  Due_amt, // Due amount
  StartDate, // Loan start date
  MaturityDate, // Loan maturity date
  Instmnt, // Number of installments
  onPay, // Payment callback function
  Loan, // Complete loan object
  pledgeNetWeight, // Weight of pledged gold (for gold loans)
  RD_redemptionDate, // Redemption date for RD
  moduleCode, // Module type (RD, GOLD, PERS, BIZ, VEH, TA, etc.)
  RD_installmentAmount, // RD installment amount
  RD_depositType, // RD deposit type
  interestAmountDue, // Interest amount due
  depositType,
  currentBalance,
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  // Card flip state - controls front/back view
  const [isFlipped, setIsFlipped] = useState(false);

  // Payment amount - initialized based on module type
  const [dueAmount, setDueAmount] = useState(
    moduleCode === "RD" ? RD_installmentAmount || 0 : Due_amt || 0
  );

  // Consolidated validation state object
  const [validationState, setValidationState] = useState({
    isInvalid: false, // General validation error
    isMinInvalid: false, // Minimum amount validation
    isGoldAmountInvalid: false, // Gold loan specific validation
    isTermAmountInvalid: false, // Term loan specific validation
    message: "", // Validation message to display
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [showGoldConfirmModal, setShowGoldConfirmModal] = useState(false);
  const [showDuesDetails, setShowDuesDetails] = useState(false);
  const [showDepositSchemeModal, setShowDepositSchemeModal] = useState(false); // Moved inside component

  // Processing state for API calls
  const [isProcessing, setIsProcessing] = useState(false);

  // ============================================================================
  // HOOKS AND REFS
  // ============================================================================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cardRef = useRef(null); // Reference to card DOM element
  const flipTimeoutRef = useRef(null); // Reference to flip timeout
  const moduleduesState = useSelector((state) => state.moduleduesState?.data);

  // ============================================================================
  // CONSTANTS
  // ============================================================================
  const FLIP_TIMEOUT = 20000; // 20 seconds auto-flip timeout
  const MIN_PAYMENT_AMOUNT = 1; // Minimum payment amount in rupees
  const TA_MIN_PAYMENT_AMOUNT = 2; // Minimum payment amount for TA module (2 rupes for test)
  const TA_MULTIPLE = 1; // TA payments must be multiples of this amount (1 rupes for test)
  const TERM_LOAN_MODULES = ["PERS", "BIZ", "VEH"]; // Term loan module codes

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  const getInitialAmount = useCallback(
    () => (moduleCode === "RD" ? RD_installmentAmount || 0 : Due_amt || 0),
    [moduleCode, RD_installmentAmount, Due_amt]
  );

  const resetValidation = useCallback(() => {
    setValidationState({
      isInvalid: false,
      isMinInvalid: false,
      isGoldAmountInvalid: false,
      isTermAmountInvalid: false,
      message: "",
    });
  }, []);

  const isTermLoan = useCallback(
    () => TERM_LOAN_MODULES.includes(moduleCode),
    [moduleCode]
  );

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================
  const validateAmount = useCallback(
    (numericValue) => {
      resetValidation();

      if (numericValue === "") return true;

      // TA module specific validation (minimum 5000, multiples of 1000)
      if (moduleCode === "TA") {
        if (numericValue < TA_MIN_PAYMENT_AMOUNT) {
          setValidationState({
            isInvalid: false,
            isMinInvalid: true,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Minimum amount of ₹${TA_MIN_PAYMENT_AMOUNT.toLocaleString()} required for TA module`,
          });
          return false;
        }

        if (numericValue % TA_MULTIPLE !== 0) {
          setValidationState({
            isInvalid: true,
            isMinInvalid: false,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Amount must be a multiple of ₹${TA_MULTIPLE.toLocaleString()}`,
          });
          return false;
        }
      }
      // General minimum amount validation (for non-TA modules)
      else if (numericValue < MIN_PAYMENT_AMOUNT) {
        setValidationState({
          isInvalid: false,
          isMinInvalid: true,
          isGoldAmountInvalid: false,
          isTermAmountInvalid: false,
          message: `Minimum amount of ₹${MIN_PAYMENT_AMOUNT} required`,
        });
        return false;
      }

      if (moduleCode === "RD" && RD_installmentAmount) {
        const isNotMultiple = numericValue % RD_installmentAmount !== 0;
        if (isNotMultiple) {
          setValidationState({
            isInvalid: true,
            isMinInvalid: false,
            isGoldAmountInvalid: false,
            isTermAmountInvalid: false,
            message: `Amount must be a multiple of installment amount ₹${RD_installmentAmount.toLocaleString()}`,
          });
          return false;
        }
      }

      return true;
    },
    [moduleCode, RD_installmentAmount, resetValidation]
  );

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleFlip = useCallback(() => {
    if (!isProcessing) {
      setIsFlipped((prev) => !prev);
    }
  }, [isProcessing]);

  const handleOutsideClick = useCallback(
    (event) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target) &&
        isFlipped
      ) {
        setIsFlipped(false);
      }
    },
    [isFlipped]
  );

  const handleInputChange = useCallback(
    (event) => {
      event.stopPropagation();
      const value = event.target.value.replace(/^0+|[^0-9]/g, "");
      const numericValue = value ? parseInt(value, 10) : "";
      validateAmount(numericValue);
      setDueAmount(value);
    },
    [validateAmount]
  );

  const proceedToPayment = useCallback(() => {
    if (onPay) {
      onPay(dueAmount);
    }

    navigate("/order/", {
      state: {
        ...Loan,
        dueAmount: parseInt(dueAmount, 10),
        moduleCode,
        accountNumber: Acc_number,
      },
    });
  }, [onPay, dueAmount, navigate, Loan, moduleCode, Acc_number]);

  // ============================================================================
  // DEPOSIT SCHEME HANDLER (MOVED OUTSIDE handlePayButtonClick)
  // ============================================================================
  const handleDepositSchemeConfirm = useCallback(
    (data) => {
      setShowDepositSchemeModal(false);
      navigate("/order/", {
        state: {
          ...Loan,
          dueAmount: parseInt(dueAmount, 10),
          moduleCode,
          accountNumber: Acc_number,
          interestType: data.interestType,
        },
      });
    },
    [Loan, dueAmount, moduleCode, Acc_number, navigate]
  );

  // ============================================================================
  // PAYMENT PROCESSING
  // ============================================================================
  const handlePayButtonClick = async (event) => {
    event.stopPropagation();

    // Get minimum amount based on module type
    const minAmount =
      moduleCode === "TA" ? TA_MIN_PAYMENT_AMOUNT : MIN_PAYMENT_AMOUNT;

    // Special handling for TA module - show deposit scheme modal
    // if (moduleCode === "TA") {
    //   setShowDepositSchemeModal(true);
    //   return;
    // }

    // Pre-validation: Check processing state and minimum amount
    if (isProcessing || !dueAmount || dueAmount < minAmount) return;

    // Set processing state to prevent multiple clicks
    setIsProcessing(true);

    try {
      // Make API call to validate payment against module dues
      await new Promise((resolve) => {
        dispatch({
          type: `${MODULEDUES}_POST`,
          payload: {
            uniqueAccountNumber: Acc_number,
            moduleCode: moduleCode,
          },
          callback: (res) => {
            if (res.is_success && res?.data) {
              const NetAmount = res?.data?.netAmount;
              const paymentAmount = parseInt(dueAmount, 10);

              // Gold loan specific validation
              if (moduleCode === "GOLD" && paymentAmount >= Due_amt) {
                setValidationState({
                  isInvalid: false,
                  isMinInvalid: false,
                  isGoldAmountInvalid: true,
                  isTermAmountInvalid: false,
                  message: `Loan closure is not available online, please enter an amount less than ₹${Due_amt?.toLocaleString()}. `,
                });
              }
              // Term loan specific validation
              else if (isTermLoan() && paymentAmount > Due_amt) {
                setValidationState({
                  isInvalid: false,
                  isMinInvalid: false,
                  isGoldAmountInvalid: false,
                  isTermAmountInvalid: true,
                  message: `Amount cannot exceed due amount of ₹${Due_amt?.toLocaleString()}`,
                });
              } else {
                // Payment amount is valid - proceed to payment
                proceedToPayment();
              }
            } else {
              // API call failed
              setValidationState({
                isInvalid: false,
                isMinInvalid: false,
                isGoldAmountInvalid: false,
                isTermAmountInvalid: true,
                message: "Failed to validate payment. Please try again.",
              });
            }
            setIsProcessing(false);
            resolve();
          },
        });
      });
    } catch (error) {
      console.error("Error processing payment:", error);
      setValidationState({
        isInvalid: false,
        isMinInvalid: false,
        isGoldAmountInvalid: false,
        isTermAmountInvalid: true,
        message:
          "An error occurred while processing payment. Please try again.",
      });
      setIsProcessing(false);
    }
  };

  // ============================================================================
  // REACT EFFECTS
  // ============================================================================
  useEffect(() => {
    if (isFlipped) {
      flipTimeoutRef.current = setTimeout(() => {
        setIsFlipped(false);
      }, FLIP_TIMEOUT);
    }

    return () => {
      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }
    };
  }, [isFlipped]);

  useEffect(() => {
    if (!isFlipped) {
      const initialAmount = getInitialAmount();
      setDueAmount(initialAmount);
      resetValidation();
    }
  }, [isFlipped, getInitialAmount, resetValidation]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [handleOutsideClick]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const isPayDisabled =
    Object.values(validationState).some((val) => val === true) ||
    !dueAmount ||
    isProcessing;

  const {
    isInvalid,
    isMinInvalid,
    isGoldAmountInvalid,
    isTermAmountInvalid,
    message,
  } = validationState;

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================
  return (
    <div className="d-flex justify-content-center align-items-center vh-10">
      <div
        ref={cardRef}
        className={`credit-card ${isFlipped ? "flipped" : ""}`}
        onClick={handleFlip}
        style={{ cursor: isProcessing ? "not-allowed" : "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleFlip();
          }
        }}
        aria-label={
          isFlipped ? "Card back - payment form" : "Card front - loan details"
        }
      >
        {/* CARD FRONT */}
        <div className="card-front">
          <Stack
            direction="horizontal"
            className="position-absolute top-0 end-0 m-2"
            gap={2}
          >
            <Badge bg="info">Tap to pay</Badge>
            {moduleCode && <Badge bg="warning">{moduleCode}</Badge>}
          </Stack>

          <div>
            {loanTypeDesc && (
              <div className="card-brand mt-2">{loanTypeDesc}</div>
            )}
            {RD_depositType && (
              <div className="card-brand mt-2">
                {moduleCode} <span></span>
                {RD_depositType}
              </div>
            )}

            <div className="card-text">
              <span className="spaced-text">A/c No:</span>
              {Acc_number}
            </div>

            <div className="card-strip"></div>

            {Disb_amt !== null && Disb_amt !== undefined && Disb_amt !== 0 && (
              <div className="card-text">
                <span>Amount&nbsp;:&nbsp;</span>₹{Disb_amt?.toLocaleString()}
              </div>
            )}

            {Due_amt && Due_amt !== 0 && (
              <div className="card-text-bold">
                <span>Due Amount : </span>₹{Due_amt?.toLocaleString()}
              </div>
            )}

            {RD_installmentAmount && (
              <div className="card-text-bold">
                <span>Installment : </span>₹
                {RD_installmentAmount?.toLocaleString()}
              </div>
            )}

            {Instmnt && (
              <div className="card-text-bold">
                <span>Total Installments : </span>
                {Instmnt}
              </div>
            )}

            {interestAmountDue && interestAmountDue !== 0 && (
              <div className="card-text">
                <span>Interest Amt. Due&nbsp;:&nbsp;</span>₹
                {interestAmountDue?.toLocaleString()}
              </div>
            )}

            {depositType && (
              <div className="card-text">
                <span>Type&nbsp;:&nbsp;</span>
                {depositType}
              </div>
            )}

            {currentBalance && (
              <div className="card-text">
                <span>Current Balance&nbsp;:&nbsp;</span>
                {currentBalance}
              </div>
            )}

            <div className="card-text">
              <span>Open Date (MM/DD/YYYY)&nbsp;:&nbsp;</span>
              {StartDate}
            </div>

            {moduleCode !== "TA" &&
              ((RD_redemptionDate && moduleCode === "RD") || MaturityDate) && (
                <div className="card-text">
                  <span>
                    {RD_redemptionDate && moduleCode === "RD"
                      ? "Redemption"
                      : "Maturity"}
                    Date (MM/DD/YYYY)&nbsp;:&nbsp;
                  </span>
                  {RD_redemptionDate && moduleCode === "RD"
                    ? RD_redemptionDate
                    : MaturityDate}
                </div>
              )}

            {pledgeNetWeight && (
              <div className="card-text">
                <span>Pledge Net Weight&nbsp;:&nbsp;</span>
                {pledgeNetWeight}
                <span>&nbsp;grams&nbsp;</span>
              </div>
            )}
          </div>
        </div>

        {/* CARD BACK */}
        <div className="card-back">
          <div className="card-back-title">
            Make Payment
            {isProcessing && <span className="ms-2">Processing...</span>}
          </div>

          {loanTypeDesc && (
            <div className="card-brand mt-1">{loanTypeDesc}</div>
          )}
          {RD_depositType && (
            <div className="card-brand mt-1">
              {moduleCode} <span></span>
              {RD_depositType}
            </div>
          )}

          <div className="card-text">
            <span className="spaced-text">A/c No:</span>
            {Acc_number}
          </div>

          <div className="card-strip"></div>

          <div className="mx-2">
            {Disb_amt !== null && Disb_amt !== undefined && Disb_amt !== 0 && (
              <div className="card-text">
                <span>Amount&nbsp;:&nbsp;</span>₹{Disb_amt?.toLocaleString()}
              </div>
            )}
            {Due_amt && (
              <div className="card-text-bold">
                <span>Total Due Amount : </span>₹{Due_amt?.toLocaleString()}
              </div>
            )}
          </div>

          <Stack direction="horizontal" gap={3}>
            <label
              htmlFor="due-amount"
              style={{ marginLeft: "1px", fontSize: "12px", minWidth: "50px" }}
            >
              Amount:
            </label>
            <input
              id="due-amount"
              type="text"
              min="1"
              placeholder={
                moduleCode === "TA"
                  ? `Min: ₹${TA_MIN_PAYMENT_AMOUNT.toLocaleString()}`
                  : "Enter amount"
              }
              onInput={(e) => {
                e.target.value = e.target.value.replace(/^0+|[^0-9]/g, "");
              }}
              onClick={handleInputChange}
              onChange={handleInputChange}
              value={dueAmount}
              disabled={isProcessing}
              className={`form-control ${
                isInvalid ||
                isMinInvalid ||
                isGoldAmountInvalid ||
                isTermAmountInvalid
                  ? "is-invalid"
                  : ""
              }`}
              style={{
                width: "100%",
                fontSize: "12px",
                display: "inline-block",
                margin: "1px",
              }}
              aria-describedby={message ? "validation-message" : undefined}
            />
            <Button
              variant="success px-4"
              size="small"
              onClick={handlePayButtonClick}
              disabled={isPayDisabled}
              aria-label={`Pay ${
                dueAmount
                  ? `₹${parseInt(dueAmount).toLocaleString()}`
                  : "amount"
              }`}
            >
              {isProcessing ? "..." : "Pay"}
            </Button>
          </Stack>

          {message && (
            <div
              id="validation-message"
              style={{ color: "red", fontSize: "11px", marginTop: "4px" }}
              role="alert"
              aria-live="polite"
            >
              {message}
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      <GoldPaymentConfirmationModal
        show={showGoldConfirmModal}
        onHide={() => {
          setShowGoldConfirmModal(false);
          setIsProcessing(false);
        }}
        onConfirm={() => {
          setShowGoldConfirmModal(false);
          proceedToPayment();
        }}
        amount={dueAmount}
        totalDue={Due_amt}
      />

      <DepositScheme
        show={showDepositSchemeModal}
        onHide={() => setShowDepositSchemeModal(false)}
        onConfirm={handleDepositSchemeConfirm}
        paymentAmount={parseInt(dueAmount, 10)}
        accountNumber={Acc_number}
      />
    </div>
  );
};

export default Flipcard;
